/* Bar Fridges Australia — card stars, for every collection grid and every
 * "featured collection" widget on the home page.
 *
 * Real browser source, copied verbatim into assets/bfau-card-stars.js by
 * build-reviews.js — same reason model-stars.browser.js and
 * model-reviews.browser.js are files rather than template literals: escaping
 * a file twice is what put a literal backslash in a regex once already.
 *
 * WHAT IT DOES
 *
 * product-grid-item7.liquid — the one card snippet every grid on the site
 * renders through, collection pages and home-page "featured collection"
 * sections alike — already carries Feefo's own
 *   <div class="feefo-product-stars-widget" data-product-sku="…">
 * Feefo keys reviews on variant SKU, so on a branded fridge (a plain cabinet
 * wearing different graphics) that div draws nothing: 201 of BFA's 389
 * fridges. This reads the SKU off that div and writes the pooled figure into
 * a plain element of its own placed beside it — Feefo's own div is hidden
 * unconditionally by CSS and never written to.
 *
 * THE SOLE RENDERER NOW, NOT A GAP-FILLER — changed 2026-08-25. The first two
 * versions tried to coexist with Feefo's own widget: render only where it
 * left a div empty, defer to it where it had already drawn something. Both
 * shipped a real bug from the same root cause — Feefo's widget gives no
 * signal for "I am finished," so any fixed wait is a guess, and on a page
 * firing many per-SKU requests at once (several home-page sliders loading
 * together) that guess was sometimes wrong: Feefo's answer landed after this
 * had already decided a div was empty and filled it, and both stars showed
 * at once. The fix is to stop needing to know when Feefo is done, by never
 * looking: every `.feefo-product-stars-widget` on the site is hidden from
 * first paint (see bfau-card-stars.liquid), and this is the only thing that
 * ever draws a star in that row.
 *
 * This also makes cards agree with everything else, which they were the one
 * exception to. The product-page rating, the review block beneath it and
 * the Frothy Finder's cards all already show the POOLED model score rather
 * than a product's own raw Feefo figure — "stars are a model score," decided
 * 2026-08-19. A card showing Feefo's own unpooled number while everywhere
 * else on the site showed the pooled one was the same kind of contradiction
 * the top-of-page stars were built to avoid at the foot of one page; now
 * there is exactly one number for a given fridge, wherever it's shown.
 *
 * BATCHING. One request per POOL, not per card — the same one-pool-covers-
 * many-cards approach frothy-pools.json already serves the product page
 * with, and the same reason: a collection page can hold 48 cards and several
 * of them are routinely siblings on one chassis. A short debounce collects a
 * burst of cards appearing together — the whole grid at page load, or one
 * infinite-scroll batch — into one round of requests rather than one per
 * card. Concurrency capped at 3, the same figure the finder's own
 * FEED_CONCURRENCY uses, for the same reason: parallel requests to one Feefo
 * origin come back short rather than erroring.
 *
 * DISPLAY ONLY. These figures must never reach structured data — see the
 * open item in bfa-ratings-system-status.md.
 */
(function () {
  var root = document.querySelector('.bfau-cs');
  if (!root) return;

  /* ---------------------------------------------------------------------
     Condition-prefixed stock: scratch-and-dent (SD-) and factory second (T2-).
     Ported from condition() + rootOf() in derive.js -- the browser has to ask
     the same question at runtime that the Node build asks, because these SKUs
     are NOT in the baked map and never will be reliably.

     They churn: a unit is listed when it arrives and gone when it sells, and
     the model code it wraps is different every time. A map keyed on SKU is
     stale for them the day it is built, so the chassis is derived here instead
     and looked up in the `_chassis` map that build-reviews.js emits alongside.

       SD-TW103-BC70B-ROADIE-AUS   scratch and dent, report TW103, of BC70B-ROADIE-AUS
       T2-A645-SC372W-VC           factory second,   report A645,  of SC372W-VC

     The token after the prefix names whoever wrote the damage report and has no
     product meaning, so it has to come off before the chassis is read. But a
     report code (A645, TW103, BA) is the same SHAPE as a model code -- EC68
     looks exactly like TW103 -- so BOTH readings are tried and whichever names
     a real chassis wins. Longest strip first: usually the next token IS the
     report code.
     --------------------------------------------------------------------- */
  var CONDITION = /^(?:SD|T2)-\s*/i;

  /* The chassis a model belongs to, read off the code alone -- same regex as
     rootOf() in derive.js. SC70-B-CCR and HUS-SC70-SS both reduce to their
     chassis; the trailing design code is exactly the part a new livery changes,
     so this keeps working on a SKU the map has never seen. */
  function rootOf(text) {
    var m = String(text || '').toUpperCase().replace(/^(HUS|DW|EC)-/, '');
    var hit = /^(?:([A-Z]+)-)?([A-Z]+\d+)/.exec(m);
    if (!hit) return m;
    return hit[1] ? hit[1] + '-' + hit[2] : hit[2];
  }

  /* A chassis whose review pool splits by finish -- SC70 only, and the same one
     entry as POOL_SPLIT in derive.js. The stainless SC70s are triple glazed and
     the black ones double glazed, so their reviews do not describe each other
     and the map is keyed SC70-B / SC70-SS rather than SC70. Read as a SEGMENT
     right after the model number, so a design code that merely contains the
     letters cannot answer. */
  var SPLIT = { SC70: 1 };
  function finishToken(text) {
    var seg = String(text || '').toUpperCase().replace(/^(HUS|DW|EC)-/, '').split('-');
    var i = -1;
    for (var n = 0; n < seg.length; n++) { if (/^SC70L?$/.test(seg[n])) { i = n; break; } }
    var next = i < 0 ? null : seg[i + 1];
    return next === 'B' || next === 'SS' ? next : null;
  }

  /* Every chassis key this SKU might be filed under, best reading first.

     A plain SKU gives one reading; a condition-prefixed one gives two, because
     the report code cannot be told from a model code by shape -- EC68 looks
     exactly like TW103. Longest strip first: usually the next token IS the
     report code.

     Each reading yields the SPLIT key before the bare root, so a scratch-and-
     dent SC70 lands on the side its finish token puts it rather than failing to
     find a bare "SC70" that the map does not carry. */
  function chassisKeys(sku) {
    var raw = String(sku || '').trim();
    var m = CONDITION.exec(raw);
    var readings = m
      ? [raw.slice(m[0].length).trim().replace(/^[A-Z]+\d*-\s*/i, ''), raw.slice(m[0].length).trim()]
      : [raw];
    var out = [];
    readings.forEach(function (c) {
      if (!c) return;
      var r = rootOf(c);
      if (!r) return;
      if (SPLIT[r]) {
        var t = finishToken(c);
        if (t && out.indexOf(r + '-' + t) < 0) out.push(r + '-' + t);
      }
      if (out.indexOf(r) < 0) out.push(r);
    });
    return out;
  }

  /* The SKU list to ask Feefo about.
     
     1. Exact match on the baked SKU map -- the pool build-reviews.js actually
        computed for this fridge, FUNCTIONAL gate and all. Always wins.
     2. `_solo` names the SKUs the build looked at and deliberately left without
        a pool, because no donor on the chassis is the same machine. Silence is
        the correct answer for those, so they stop here. Without this step they
        are indistinguishable from a SKU the build never saw, and 16 of them --
        SK86R-SD, SK245-SD, SG2H-SD and the like -- would take the chassis pool
        and show a heated-glass fridge's reviews on a solid-door card.
     3. The chassis map. This is what carries condition-prefixed stock (SD-/T2-)
        and any design added since the last build.
     4. Own SKU. Never an EMPTY list: Feefo answers a blank product_sku with the
        MERCHANT-WIDE score, which would put the shop's overall rating on an
        unreviewed fridge. */
  function poolFor(sku, map) {
    if (!sku) return sku;
    if (map[sku]) return map[sku];
    var solo = map._solo || [];
    if (solo.indexOf(sku) >= 0) return sku;
    var byChassis = map._chassis || {};
    var keys = chassisKeys(sku);
    for (var i = 0; i < keys.length; i++) {
      if (byChassis[keys[i]]) return byChassis[keys[i]];
    }
    return sku;
  }


  /* The same frothy-pools.json the product page reads — SKU to the
     comma-separated SKU list whose reviews describe it. One asset, one
     definition of "which fridges share a chassis," so a card here and the
     rating at the top of that fridge's own product page cannot disagree.
     A second fetch of it is free: one CDN file, already warmed by whichever
     page hit it first this session. Reshaped once into { pools, chassis }
     so every batch reuses the same reverse index rather than rebuilding it. */
  var poolsPromise = fetch(root.getAttribute('data-pools'), { credentials: 'omit' })
    .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
    .catch(function () { return {}; })
    .then(function (pools) { return pools; });

  /* Feefo's own summary endpoint, as their widget builds it. since_period=all
     is load-bearing — omit it and the API answers zero for a chassis that
     plainly has reviews, with no error to notice (see the API gotcha in
     bfa-ratings-system-status.md). Deliberately duplicated rather than
     shared across the three browser files: each ships as an independent
     asset and any one of them may be installed without the others. */
  var Q = '?since_period=all&unanswered_feedback=include'
    + '&merchant_identifier=bar-fridges-australia&origin=bar-fridges-australia.com.au'
    + '&source=on_page_product_integration';
  var SUMMARY = 'https://api.feefo.com/api/10/reviews/summary/product' + Q;

  /* Scoped to .pr-info — the wrapper product-grid-item7.liquid renders the
     card's Feefo div inside — not a bare .feefo-product-stars-widget
     selector. This script's own config div loads from
     bfau-customisations.liquid, which renders site-wide, INCLUDING the
     product page, which carries its own separate Feefo div (inside
     .t4s-product_meta) that bfau-model-stars.js already owns. An unscoped
     selector here would render this script's figure a second time on that
     div too — a double-up between our own two features, on top of the one
     with Feefo this file exists to avoid. */
  var SELECTOR = '.pr-info .feefo-product-stars-widget[data-product-sku]';
  var CONCURRENCY = 3;
  /* Only a batching window, not a wait for Feefo — collects a burst of cards
     appearing together (the whole grid at page load, or one infinite-scroll
     page) into one round of pooled requests instead of one per card. */
  var DEBOUNCE = 200;

  var timer = null;

  function markSeen(el) { el.setAttribute('data-bfau-cs', '1'); }

  function unseen() {
    return Array.prototype.filter.call(document.querySelectorAll(SELECTOR),
      function (el) { return !el.hasAttribute('data-bfau-cs'); });
  }

  function starStyle(r) {
    var v = Math.max(0, Math.min(5, r || 0));
    var n = Math.floor(v);
    return '--bfau-star-n:' + n + ';--bfau-star-frac:' + (v - n).toFixed(2);
  }

  /* The hover breakdown — one panel shared by every card, the same markup
     contract as the finder's cards and the product page's top-of-page
     stars (`.bfau-pop-t` / `-r` / `-l` / `-n` / `-bar`, from star-css.js).
     A shopper who hovers a card and then a product page must not meet a
     different-looking panel. Missing entirely in the first two versions of
     this file — only the finder and the product page had it — added
     2026-08-25 for parity across every surface that shows a star. */
  var pop = null;
  function ensurePop() {
    if (!pop) {
      pop = document.createElement('div');
      pop.className = 'bfau-cs-pop';
      pop.setAttribute('role', 'tooltip');
      pop.hidden = true;
      document.body.appendChild(pop);
    }
    return pop;
  }

  /* The star levels these figures count, and the ONE thing that must read the
     same on all three surfaces -- the grid card, this rating, and the review
     section at the foot of the page. It is asserted in build-reviews.js, for
     the reason every other invariant here is: three copies of a browser file
     that quietly drift apart fail only on a product page, as a card saying
     (25) above a section showing 22.

     1- and 2-star reviews are not quoted in the section below, so they are not
     counted here either. The SCORE is deliberately still Feefo's, over every
     review including those -- see the note in model-reviews.browser.js. That is
     why the count is not "what the score is based on" and no longer says so. */
  var SHOWN = '3,4,5';
  function isShown(level) { return SHOWN.split(',').indexOf(String(level)) >= 0; }

  function bars(hist, count) {
    var h = '';
    for (var i = 5; i >= 1; i--) {
      var n = hist[i] || 0;
      var w = count ? (n / count) * 100 : 0;
      h += '<div class="bfau-pop-r"><span class="bfau-pop-l">' + i + ' Star'
        + (i === 1 ? '' : 's') + '</span><span class="bfau-pop-n">' + n + '</span>'
        + '<span class="bfau-pop-bar"><i style="width:' + w.toFixed(1) + '%"></i></span></div>';
    }
    return h;
  }

  /* Positioned on open rather than by CSS, same reason the product page's
     panel is: fixed coordinates are the only ones that survive the anchor
     being anywhere in a grid of cards, and appended to <body> rather than
     the card because a card's own overflow would clip it. */
  function openPop(anchor, data) {
    var p = ensurePop();
    var score = data.rating % 1 === 0 ? String(data.rating) : data.rating.toFixed(1);
    p.innerHTML = '<p class="bfau-pop-t">' + score + '/5 Stars</p>' + bars(data.hist, data.count);
    p.hidden = false;
    var a = anchor.getBoundingClientRect();
    var w = p.offsetWidth;
    var left = a.left + a.width / 2 - w / 2;
    left = Math.max(8, Math.min(left, document.documentElement.clientWidth - w - 8));
    var below = a.bottom + 10;
    var fits = below + p.offsetHeight < window.innerHeight - 8;
    p.style.left = left + 'px';
    p.style.top = (fits ? below : Math.max(8, a.top - p.offsetHeight - 10)) + 'px';
  }

  function closePop() { if (pop) pop.hidden = true; }

  /* A sibling element, not content written inside Feefo's own div — that div
     is hidden unconditionally by CSS (bfau-card-stars.liquid) and nothing
     needs to know or care what, if anything, Feefo eventually puts in it.

     data is { rating, count, hist } — the same shape request() resolves,
     so every card sharing a pool gets the identical object rather than a
     fresh copy each, which is also what lets the popover show the SAME
     histogram on two cards of the same chassis. */
  function render(el, data) {
    var score = data.rating % 1 === 0 ? String(data.rating) : data.rating.toFixed(1);
    /* Not "based on N reviews": N is how many the product page quotes, and the
       score is Feefo's over all of them. See SHOWN. */
    var label = score + ' out of 5. ' + data.count + ' review'
      + (data.count === 1 ? '' : 's') + ' of this model.';
    var wrap = document.createElement('span');
    wrap.className = 'bfau-cs-fill';
    wrap.tabIndex = 0;
    wrap.setAttribute('aria-label', label);
    wrap.innerHTML = '<span class="bfau-cs-stars" style="' + starStyle(data.rating)
      + '" aria-hidden="true"><i></i></span><span class="bfau-cs-count">(' + data.count + ')</span>';
    wrap.addEventListener('mouseenter', function () { openPop(wrap, data); });
    wrap.addEventListener('mouseleave', closePop);
    /* Keyboard reaches it too: the panel is the only place the breakdown is. */
    wrap.addEventListener('focus', function () { openPop(wrap, data); });
    wrap.addEventListener('blur', closePop);
    el.insertAdjacentElement('afterend', wrap);
  }

  function request(pool) {
    return fetch(SUMMARY + '&product_sku=' + encodeURIComponent(pool), { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (j) {
        var rt = j && j.rating;
        var pr = (rt && rt.product) || {};
        var rating = (rt && rt.rating) || 0;
        /* Summed from the SHOWN levels, not pr.count: a card must say the same
           number the product page it links to will say. */
        var hist = {}, count = 0;
        for (var i = 1; i <= 5; i++) {
          hist[i] = isShown(i) ? (pr[i + '_star'] || 0) : 0;
          count += hist[i];
        }
        /* Nothing quoted on this chassis: no fill, and the card keeps whatever
           Feefo's own widget draws. */
        if (!count || !rating) return null;
        return { rating: rating, count: count, hist: hist };
      });
  }

  /* Groups the divs by pool string and works through the groups with a
     fixed-width worker pool, exactly as build-reviews.js's own comment
     describes doing it for the finder: a list unique to one card is a
     request unique to one card, and the whole point of pooling is that it
     usually isn't. Marked seen up front, not after the request resolves —
     a card is spoken for the moment it enters a batch, so a mutation
     observed while its request is still in flight can never queue it a
     second time. */
  function fill(candidates, data) {
    var groups = {};
    candidates.forEach(function (el) {
      markSeen(el);
      var sku = el.getAttribute('data-product-sku');
      if (!sku) return;
      /* Exact match first — the baked, functional-matched pool
         frothy-pools.json actually computed for this SKU. Failing that, the
         chassis map, which carries condition-prefixed stock (SD-/T2-) and any
         design added since the last build. Only a totally unknown chassis
         falls back to asking Feefo about the bare SKU alone, same as it
         always has. */
      var pool = poolFor(sku, data);
      (groups[pool] || (groups[pool] = [])).push(el);
    });

    var keys = Object.keys(groups);
    var i = 0;
    function next() {
      if (i >= keys.length) return;
      var pool = keys[i++];
      var els = groups[pool];
      request(pool)
        .then(function (data) {
          /* No data: nothing to show, same as Feefo's own widget would have
             left — the chassis genuinely has no reviews. */
          if (data) els.forEach(function (el) { render(el, data); });
        })
        .catch(function () {})
        .then(next);
    }
    for (var c = 0; c < CONCURRENCY; c++) next();
  }

  function schedule() {
    if (!unseen().length) return;
    clearTimeout(timer);
    timer = setTimeout(function () {
      var candidates = unseen();
      if (!candidates.length) return;
      poolsPromise.then(function (data) { fill(candidates, data); });
    }, DEBOUNCE);
  }

  schedule();
  /* The only way to catch cards a facets.js infinite-scroll fetch drops into
     the grid after load. */
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
})();
